
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import { QuoteIcon } from 'lucide-react';

interface TestimonialProps {
  quote: string;
  name: string;
  title: string;
  company: string;
  imgUrl?: string;
  industry: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ 
  quote, 
  name, 
  title, 
  company, 
  imgUrl,
  industry 
}) => {
  return (
    <Card className="h-full">
      <CardContent className="p-6">
        <div className="flex flex-col h-full">
          <Badge variant="outline" className="self-start mb-4">{industry}</Badge>
          
          <div className="mb-6 text-gray-600 flex">
            <QuoteIcon className="h-6 w-6 mr-2 text-medihub-primary/60 flex-shrink-0" />
            <p className="italic">{quote}</p>
          </div>
          
          <div className="mt-auto pt-4 border-t border-gray-100 flex items-center">
            <Avatar className="h-10 w-10 mr-3">
              {imgUrl ? (
                <AvatarImage src={imgUrl} alt={name} />
              ) : (
                <AvatarFallback>{name.charAt(0)}</AvatarFallback>
              )}
            </Avatar>
            <div>
              <h4 className="font-semibold text-gray-900">{name}</h4>
              <p className="text-sm text-gray-500">{title}, {company}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const IndustryTestimonials = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-2">Sikertörténetek</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Mit mondanak ügyfeleink?</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Ismerje meg, hogyan segítettünk különböző szakmai területeken az események hatékony szervezésében.
          </p>
        </div>

        <Tabs defaultValue="jog" className="w-full">
          <TabsList className="flex flex-wrap justify-center bg-transparent h-auto p-0 mb-8">
            <TabsTrigger value="jog" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Jogi szakma
            </TabsTrigger>
            <TabsTrigger value="muszaki" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Mérnöki terület
            </TabsTrigger>
            <TabsTrigger value="oktatas" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Oktatás
            </TabsTrigger>
            <TabsTrigger value="uzleti" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Üzleti élet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jog" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Testimonial 
                quote="A rendszer jelentősen megkönnyítette a kötelező továbbképzések szervezését és a kreditpontok nyilvántartását. Ügyfeleink egyszerűen találják meg a számukra releváns eseményeket."
                name="Dr. Kovács János"
                title="Elnök"
                company="Regionális Ügyvédi Kamara"
                industry="Jogi szektor"
              />
              
              <Testimonial 
                quote="Korábban hetekig tartott egy konferencia szervezése és a résztvevők kezelése. Most mindez néhány óra alatt megtörténik, és sokkal kevesebb adminisztrációval jár."
                name="Dr. Nagy Erzsébet"
                title="Rendezvényszervező"
                company="Adójogi Egyesület"
                industry="Jogi szektor"
              />
              
              <Testimonial 
                quote="Az automatikus értesítések és a könnyű regisztrációs folyamat nagy népszerűségnek örvend tagjaink körében. Jelentősen növekedett a részvételi arányunk a képzéseinken."
                name="Dr. Szabó Péter"
                title="Főtitkár"
                company="Magyar Közjegyzői Kamara"
                industry="Jogi szektor"
              />
            </div>
          </TabsContent>

          <TabsContent value="muszaki" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Testimonial 
                quote="A platform segítségével átláthatóbbá váltak a mérnöki továbbképzések, és a kollégák könnyen megtalálják a szakterületükhöz kapcsolódó eseményeket."
                name="Mérnök Tamás"
                title="Elnökhelyettes"
                company="Mérnöki Kamara"
                industry="Mérnöki szektor"
              />
              
              <Testimonial 
                quote="A szoftver pontosan követi a kötelező szakmai továbbképzések rendszerét, és automatikusan kezeli a kreditpontokat is. Tökéletes megoldás számunkra."
                name="Tóth Barbara"
                title="Továbbképzési vezető"
                company="Magyar Építészek Szövetsége"
                industry="Mérnöki szektor"
              />
              
              <Testimonial 
                quote="Az informatikai konferenciáink szervezése sokkal gördülékenyebb lett. A rendszer kezelni tudja a párhuzamos szekciók és workshopok bonyolult struktúráját is."
                name="Nagy Gábor"
                title="Szervező"
                company="IT Szakmai Egyesület"
                industry="Mérnöki szektor"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="oktatas" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Testimonial 
                quote="Az egyetemi konferenciáink szervezése és az absztraktok kezelése rendkívül egyszerűvé vált a platform használatával. Jelentős időt és erőforrást takarítunk meg."
                name="Dr. Molnár Zsófia"
                title="Egyetemi docens"
                company="Tudományegyetem"
                industry="Oktatás"
              />
              
              <Testimonial 
                quote="A pedagógus továbbképzések akkreditációs követelményeit tökéletesen kezeli a rendszer. Az automatikus pontszámítás és a jelenléti ívek kezelése nagy segítség."
                name="Pintér Katalin"
                title="Képzési koordinátor"
                company="Pedagógiai Oktatási Központ"
                industry="Oktatás"
              />
              
              <Testimonial 
                quote="Tudományos társaságunk nemzetközi konferenciáit sokkal hatékonyabban tudjuk szervezni. A többnyelvű felület és az absztrakt kezelő különösen hasznos számunkra."
                name="Prof. Fehér István"
                title="Elnök"
                company="Magyar Tudományos Társaság"
                industry="Oktatás"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="uzleti" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Testimonial 
                quote="A céges workshopjaink és ügyfél-eseményeink szervezése sokkal professzionálisabb lett. A rendszer minden részletre kiterjed, és a résztvevők elégedettsége is nőtt."
                name="Horváth Ágnes"
                title="Marketingvezető"
                company="Nagyvállalat Rt."
                industry="Üzleti szektor"
              />
              
              <Testimonial 
                quote="Képzési programjaink átláthatóbbá váltak, és a rendszer segít a céges kompetenciamátrix követésében is. Az automatikus jelentkezési folyamat sok adminisztrációt megtakarít."
                name="Varga Zoltán"
                title="HR igazgató"
                company="Szolgáltató Kft."
                industry="Üzleti szektor"
              />
              
              <Testimonial 
                quote="Iparági konferenciáink szervezése és a szponzori csomagok kezelése rendkívül egyszerűvé vált. A platform tökéletesen illeszkedik a speciális igényeinkhez."
                name="Simon Judit"
                title="Rendezvényszervező"
                company="Üzleti Egyesület"
                industry="Üzleti szektor"
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default IndustryTestimonials;
